import React from 'react';

import { cn } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { formatDate } from 'lib/helpers/dateUtils';

import Button from '../Button/Button';
import Popover from '../Popovers/Popover/Popover';

import { getOptionsByDirection } from './lib/helpers';
import DateRangeContent from './components/DateRangeContent';
import { anchorRangeToZone, toCalendarDayRange } from './lib/dayBoundaries';

export { default as DateRangeContent } from './components/DateRangeContent';
export {
  getDateRange,
  getDateRangeByUnit,
  getDateOptions,
  getFutureDateOptions,
  getAllDateOptions
} from './lib/helpers';

const DateRangeSelector = ({
  onChange,
  value,
  buttonClassName,
  buttonTextClassName,
  buttonIcon,
  buttonIconColor = 'neutral',
  position,
  direction,
  dateOnly = false
}) => {
  const timezone = useTimezone();
  const options = getOptionsByDirection(direction, timezone);

  // Anchored before formatting so the label reads the same boundaries the picker shows and the query
  // uses. Without this a default filter's bare date renders with no time at all, while every range the
  // user picks renders with one.
  // A date-only range must render as calendar days — never anchored into instants, and coerced in case
  // the stored value still is one.
  const displayValue = dateOnly
    ? toCalendarDayRange(value, timezone)
    : anchorRangeToZone(value, timezone);

  const buttonText =
    value?.id !== 'custom-date-range' ? (
      options.find((option) => option.id === value?.id)?.label || value?.label || 'All Time'
    ) : (
      <>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(displayValue?.startDate, timezone)}
        </label>
        <span className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>-</span>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(displayValue?.endDate, timezone)}
        </label>
      </>
    );

  return (
    <Popover
      panelClassName="p-0 min-w-[244px] w-fit"
      position={position}
      isFixed
      customButtonTrigger={({ open }) => (
        <Button
          variant="tertiary"
          size="xs"
          text={buttonText}
          icon={buttonIcon}
          className={cn('whitespace-nowrap', open && 'bg-neutral-100', buttonClassName)}
          textClassName={buttonTextClassName}
          iconColor={buttonIconColor}
        />
      )}>
      {({ closePopover }) => (
        <DateRangeContent
          onChange={onChange}
          value={value}
          onClose={closePopover}
          direction={direction}
          dateOnly={dateOnly}
        />
      )}
    </Popover>
  );
};

export default DateRangeSelector;
